import React from "react";
import "./styles.css";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import RestoreFromTrashIcon from "@material-ui/icons/RestoreFromTrash";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import CityMapField from "../../Components/CityMapField";

const baseUrl = "https://api.appetito.ro/";

// Turda and Câmpia Turzii are each their own "main" city in the data (see
// Components/Navbar/CitySelect.js for the same special case on the
// dropdown), but they're also shown again in the secondary-cities table
// below so the whole city hierarchy is visible in one place — they stay
// read-only there and are managed from the main-cities table instead.
const TURDA_CAMPIA_NAMES = ["Turda", "Câmpia Turzii"];

function emptyMainForm() {
  return {name: "", latitude: "", longitude: "", area: ""};
}

function emptySecondaryForm() {
  return {name: "", main_city_id: "", latitude: "", longitude: "", area: ""};
}

export default class Orase extends React.Component {
  state = {
    cities: [],
    loading: true,
    showDeleted: false,
    mainDialogOpen: false,
    editingMainCityId: null,
    mainForm: emptyMainForm(),
    mainSaving: false,
    secondaryDialogOpen: false,
    editingSecondaryCityId: null,
    secondaryForm: emptySecondaryForm(),
    secondarySaving: false,
    priorityDialogCity: null,
    priorityForm: {enabled: false, fee: "", time: ""},
    prioritySaving: false,
  };

  componentDidMount() {
    this.loadCities();
  }

  authHeaders = () => ({Authorization: `Bearer ${localStorage.getItem("access_token")}`});

  loadCities = async () => {
    this.setState({loading: true});
    try {
      const response = await axios.get(`${baseUrl}v1/admin/cities`, {
        params: {includeDeleted: true},
        headers: this.authHeaders(),
      });
      this.setState({cities: response.data || []});
    } catch (e) {
      alert("Eroare la încărcarea orașelor.");
    } finally {
      this.setState({loading: false});
    }
  };

  toggleShowDeleted = () => {
    this.setState({showDeleted: !this.state.showDeleted});
  };

  // --- main cities ---

  openAddMainCityDialog = () => {
    this.setState({mainDialogOpen: true, editingMainCityId: null, mainForm: emptyMainForm()});
  };

  openEditMainCityDialog = (city) => {
    this.setState({
      mainDialogOpen: true,
      editingMainCityId: city.id,
      mainForm: {
        name: city.name || "",
        latitude: city.latitude || "",
        longitude: city.longitude || "",
        area: city.area || "",
      },
    });
  };

  closeMainCityDialog = () => {
    this.setState({mainDialogOpen: false, editingMainCityId: null});
  };

  updateMainFormField = (field) => (event) => {
    this.setState({mainForm: {...this.state.mainForm, [field]: event.target.value}});
  };

  setMainLatLng = (lat, lng) => {
    this.setState({mainForm: {...this.state.mainForm, latitude: lat, longitude: lng}});
  };

  saveMainCity = async () => {
    const {editingMainCityId, mainForm} = this.state;
    if (!mainForm.name.trim()) {
      alert("Completează numele orașului.");
      return;
    }
    const payload = {
      name: mainForm.name.trim(),
      latitude: parseFloat(mainForm.latitude) || 0,
      longitude: parseFloat(mainForm.longitude) || 0,
      area: parseFloat(mainForm.area) || 0,
      main_city: true,
    };

    this.setState({mainSaving: true});
    try {
      if (editingMainCityId) {
        await axios.put(`${baseUrl}v1/admin/cities/${editingMainCityId}`, payload, {headers: this.authHeaders()});
      } else {
        await axios.post(`${baseUrl}v1/admin/cities`, payload, {headers: this.authHeaders()});
      }
      await this.loadCities();
      this.closeMainCityDialog();
    } catch (e) {
      alert("Eroare la salvarea orașului.");
    } finally {
      this.setState({mainSaving: false});
    }
  };

  deleteMainCity = async (city) => {
    if (!window.confirm(`Ștergi orașul ${city.name}?`)) {
      return;
    }
    try {
      await axios.delete(`${baseUrl}v1/admin/cities/${city.id}`, {headers: this.authHeaders()});
      await this.loadCities();
    } catch (e) {
      alert("Eroare la ștergerea orașului.");
    }
  };

  recoverMainCity = async (city) => {
    try {
      await axios.post(`${baseUrl}v1/admin/cities/${city.id}/recover`, {}, {headers: this.authHeaders()});
      await this.loadCities();
    } catch (e) {
      alert("Eroare la recuperarea orașului.");
    }
  };

  // --- priority delivery (main cities only; never offered in secondary cities) ---

  openPriorityDialog = (city) => {
    this.setState({
      priorityDialogCity: city,
      priorityForm: {
        enabled: Boolean(city.priority_delivery_enabled),
        fee: city.priority_delivery_fee || "",
        time: city.priority_delivery_time || "",
      },
    });
  };

  closePriorityDialog = () => {
    this.setState({priorityDialogCity: null});
  };

  updatePriorityFormField = (field) => (event) => {
    const value = field === "enabled" ? event.target.checked : event.target.value;
    this.setState({priorityForm: {...this.state.priorityForm, [field]: value}});
  };

  savePriority = async () => {
    const {priorityDialogCity, priorityForm} = this.state;
    const fee = parseFloat(priorityForm.fee) || 0;
    if (priorityForm.enabled && fee <= 0) {
      alert("Completează taxa pentru livrarea prioritară.");
      return;
    }
    this.setState({prioritySaving: true});
    try {
      await axios.put(
        `${baseUrl}v1/admin/cities/${priorityDialogCity.id}/priority-delivery`,
        {enabled: priorityForm.enabled, fee, time: priorityForm.time.trim()},
        {headers: this.authHeaders()}
      );
      await this.loadCities();
      this.closePriorityDialog();
    } catch (e) {
      alert("Eroare la salvarea livrării prioritare.");
    } finally {
      this.setState({prioritySaving: false});
    }
  };

  // --- secondary cities ---

  openAddSecondaryCityDialog = () => {
    this.setState({secondaryDialogOpen: true, editingSecondaryCityId: null, secondaryForm: emptySecondaryForm()});
  };

  openEditSecondaryCityDialog = (city) => {
    this.setState({
      secondaryDialogOpen: true,
      editingSecondaryCityId: city.id,
      secondaryForm: {
        name: city.name || "",
        main_city_id: city.main_city_id || "",
        latitude: city.latitude || "",
        longitude: city.longitude || "",
        area: city.area || "",
      },
    });
  };

  closeSecondaryCityDialog = () => {
    this.setState({secondaryDialogOpen: false, editingSecondaryCityId: null});
  };

  updateSecondaryFormField = (field) => (event) => {
    this.setState({secondaryForm: {...this.state.secondaryForm, [field]: event.target.value}});
  };

  setSecondaryLatLng = (lat, lng) => {
    this.setState({secondaryForm: {...this.state.secondaryForm, latitude: lat, longitude: lng}});
  };

  saveSecondaryCity = async () => {
    const {editingSecondaryCityId, secondaryForm} = this.state;
    if (!secondaryForm.name.trim() || !secondaryForm.main_city_id) {
      alert("Completează numele și orașul principal.");
      return;
    }
    const payload = {
      name: secondaryForm.name.trim(),
      main_city: false,
      main_city_id: parseInt(secondaryForm.main_city_id, 10),
      latitude: parseFloat(secondaryForm.latitude) || 0,
      longitude: parseFloat(secondaryForm.longitude) || 0,
      area: parseFloat(secondaryForm.area) || 0,
    };

    this.setState({secondarySaving: true});
    try {
      if (editingSecondaryCityId) {
        await axios.put(`${baseUrl}v1/admin/cities/${editingSecondaryCityId}`, payload, {headers: this.authHeaders()});
      } else {
        await axios.post(`${baseUrl}v1/admin/cities`, payload, {headers: this.authHeaders()});
      }
      await this.loadCities();
      this.closeSecondaryCityDialog();
    } catch (e) {
      alert("Eroare la salvarea orașului.");
    } finally {
      this.setState({secondarySaving: false});
    }
  };

  deleteSecondaryCity = async (city) => {
    if (!window.confirm(`Ștergi orașul ${city.name}?`)) {
      return;
    }
    try {
      await axios.delete(`${baseUrl}v1/admin/cities/${city.id}`, {headers: this.authHeaders()});
      await this.loadCities();
    } catch (e) {
      alert("Eroare la ștergerea orașului.");
    }
  };

  recoverSecondaryCity = async (city) => {
    try {
      await axios.post(`${baseUrl}v1/admin/cities/${city.id}/recover`, {}, {headers: this.authHeaders()});
      await this.loadCities();
    } catch (e) {
      alert("Eroare la recuperarea orașului.");
    }
  };

  render() {
    const {
      cities,
      loading,
      showDeleted,
      mainDialogOpen,
      editingMainCityId,
      mainForm,
      mainSaving,
      secondaryDialogOpen,
      editingSecondaryCityId,
      secondaryForm,
      secondarySaving,
      priorityDialogCity,
      priorityForm,
      prioritySaving,
    } = this.state;

    const visible = (city) => showDeleted || !city.deleted;

    const mainCities = cities
      .filter((city) => city.main_city && visible(city))
      .sort((a, b) => a.name.localeCompare(b.name, "ro"));

    const activeMainCities = cities
      .filter((city) => city.main_city && !city.deleted)
      .sort((a, b) => a.name.localeCompare(b.name, "ro"));

    const pinnedRows = cities.filter((city) => city.main_city && TURDA_CAMPIA_NAMES.includes(city.name) && visible(city));
    const secondaryRows = cities.filter((city) => !city.main_city && visible(city));
    const secondaryTableRows = [...pinnedRows, ...secondaryRows].sort((a, b) => a.name.localeCompare(b.name, "ro"));

    const mainCityName = (mainCityId) => {
      const match = cities.find((city) => city.id === mainCityId);
      return match ? match.name : "—";
    };

    return (
      <div className="settings-container">
        <h2 className="settings-title">Orașe</h2>

        <FormControlLabel
          control={<Switch checked={showDeleted} onChange={this.toggleShowDeleted} color="primary"/>}
          label="Arată orașele șterse"
          style={{marginBottom: 16}}
        />

        {loading ? (
          <div className="loader-container">
            <div className="loader"/>
          </div>
        ) : (
          <>
            <div className="settings-section settings-section-wide">
              <div className="settings-section-header">
                <h3 className="settings-section-title">Orașe principale</h3>
                <IconButton color="primary" onClick={this.openAddMainCityDialog} title="Adaugă oraș principal">
                  <AddIcon/>
                </IconButton>
              </div>
              <span className="settings-description">
                Orașele principale apar în selectorul de oraș și sunt folosite pentru detectarea automată a orașului după locație.
              </span>

              <Table size="small" style={{marginTop: 16}}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nume</TableCell>
                    <TableCell>Latitudine</TableCell>
                    <TableCell>Longitudine</TableCell>
                    <TableCell>Rază (km)</TableCell>
                    <TableCell>Livrare prioritară</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mainCities.map((city) => (
                    <TableRow key={city.id} className={city.deleted ? "settings-table-row-deleted" : ""}>
                      <TableCell>{city.name}</TableCell>
                      <TableCell>{city.latitude || "—"}</TableCell>
                      <TableCell>{city.longitude || "—"}</TableCell>
                      <TableCell>{city.area || "—"}</TableCell>
                      <TableCell>
                        {city.priority_delivery_enabled
                          ? `Activă · ${city.priority_delivery_fee} lei${city.priority_delivery_time ? ` · ${city.priority_delivery_time}` : ""}`
                          : "Inactivă"}
                      </TableCell>
                      <TableCell align="right">
                        {city.deleted ? (
                          <IconButton size="small" title="Recuperează" onClick={() => this.recoverMainCity(city)}>
                            <RestoreFromTrashIcon fontSize="small"/>
                          </IconButton>
                        ) : (
                          <>
                            <IconButton size="small" title="Livrare prioritară" onClick={() => this.openPriorityDialog(city)}>
                              <FlashOnIcon fontSize="small"/>
                            </IconButton>
                            <IconButton size="small" title="Editează" onClick={() => this.openEditMainCityDialog(city)}>
                              <EditIcon fontSize="small"/>
                            </IconButton>
                            <IconButton size="small" title="Șterge" onClick={() => this.deleteMainCity(city)}>
                              <DeleteIcon fontSize="small"/>
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {mainCities.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} style={{textAlign: "center", color: "#888"}}>
                        Niciun oraș principal.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="settings-section settings-section-wide">
              <div className="settings-section-header">
                <h3 className="settings-section-title">Orașe secundare</h3>
                <IconButton color="primary" onClick={this.openAddSecondaryCityDialog} title="Adaugă oraș secundar">
                  <AddIcon/>
                </IconButton>
              </div>
              <span className="settings-description">
                Orașe/localități legate de un oraș principal (ex: sate din jurul unui oraș principal).
                Turda și Câmpia Turzii apar și aici pentru vizibilitate, dar se gestionează din tabelul de mai sus.
              </span>

              <Table size="small" style={{marginTop: 16}}>
                <TableHead>
                  <TableRow>
                    <TableCell>Nume</TableCell>
                    <TableCell>Oraș principal</TableCell>
                    <TableCell>Rază (km)</TableCell>
                    <TableCell align="right"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {secondaryTableRows.map((city) => {
                    const isPinnedMainCity = city.main_city;
                    return (
                      <TableRow key={city.id} className={city.deleted ? "settings-table-row-deleted" : ""}>
                        <TableCell>{city.name}</TableCell>
                        <TableCell>{isPinnedMainCity ? "— (oraș principal)" : mainCityName(city.main_city_id)}</TableCell>
                        <TableCell>{city.area || "—"}</TableCell>
                        <TableCell align="right">
                          {isPinnedMainCity ? (
                            <span className="settings-description">gestionează mai sus</span>
                          ) : city.deleted ? (
                            <IconButton size="small" title="Recuperează" onClick={() => this.recoverSecondaryCity(city)}>
                              <RestoreFromTrashIcon fontSize="small"/>
                            </IconButton>
                          ) : (
                            <>
                              <IconButton size="small" title="Editează" onClick={() => this.openEditSecondaryCityDialog(city)}>
                                <EditIcon fontSize="small"/>
                              </IconButton>
                              <IconButton size="small" title="Șterge" onClick={() => this.deleteSecondaryCity(city)}>
                                <DeleteIcon fontSize="small"/>
                              </IconButton>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {secondaryTableRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} style={{textAlign: "center", color: "#888"}}>
                        Niciun oraș secundar.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        <Dialog open={mainDialogOpen} onClose={this.closeMainCityDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingMainCityId ? "Editează oraș principal" : "Adaugă oraș principal"}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nume"
              fullWidth
              margin="normal"
              value={mainForm.name}
              onChange={this.updateMainFormField("name")}
            />
            <CityMapField
              latitude={mainForm.latitude}
              longitude={mainForm.longitude}
              area={mainForm.area}
              onChangeLatLng={this.setMainLatLng}
            />
            <TextField
              label="Rază (km)"
              type="number"
              fullWidth
              margin="normal"
              value={mainForm.area}
              onChange={this.updateMainFormField("area")}
              helperText="Folosită pentru detectarea automată a orașului după locație. Poate fi completată ulterior."
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeMainCityDialog} disabled={mainSaving}>
              Anulează
            </Button>
            <Button onClick={this.saveMainCity} color="primary" variant="contained" disabled={mainSaving}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Boolean(priorityDialogCity)} onClose={this.closePriorityDialog} fullWidth maxWidth="xs">
          <DialogTitle>Livrare prioritară{priorityDialogCity ? ` — ${priorityDialogCity.name}` : ""}</DialogTitle>
          <DialogContent>
            <span className="settings-description">
              Opțiune plătită în checkout-ul web, disponibilă doar pentru adrese din acest oraș — nu și
              pentru orașele secundare (satele) legate de el.
              Comenzile în care clientul a ales-o deja sunt taxate chiar dacă opțiunea e dezactivată între timp.
            </span>
            <FormControlLabel
              control={
                <Switch
                  checked={priorityForm.enabled}
                  onChange={this.updatePriorityFormField("enabled")}
                  color="primary"
                />
              }
              label="Activă"
              style={{display: "block", marginTop: 12}}
            />
            <TextField
              label="Taxă (lei)"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{min: 0, step: 0.01}}
              value={priorityForm.fee}
              onChange={this.updatePriorityFormField("fee")}
            />
            <TextField
              label="Timp livrare afișat"
              fullWidth
              margin="normal"
              placeholder="ex: 40–50 min"
              value={priorityForm.time}
              onChange={this.updatePriorityFormField("time")}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closePriorityDialog} disabled={prioritySaving}>
              Anulează
            </Button>
            <Button onClick={this.savePriority} color="primary" variant="contained" disabled={prioritySaving}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={secondaryDialogOpen} onClose={this.closeSecondaryCityDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editingSecondaryCityId ? "Editează oraș secundar" : "Adaugă oraș secundar"}</DialogTitle>
          <DialogContent>
            <TextField
              label="Nume"
              fullWidth
              margin="normal"
              value={secondaryForm.name}
              onChange={this.updateSecondaryFormField("name")}
            />
            <TextField
              select
              label="Oraș principal"
              fullWidth
              margin="normal"
              value={secondaryForm.main_city_id}
              onChange={this.updateSecondaryFormField("main_city_id")}
            >
              <MenuItem value="">Alege oraș principal</MenuItem>
              {activeMainCities.map((city) => (
                <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
              ))}
            </TextField>
            <CityMapField
              latitude={secondaryForm.latitude}
              longitude={secondaryForm.longitude}
              area={secondaryForm.area}
              onChangeLatLng={this.setSecondaryLatLng}
            />
            <TextField
              label="Rază (km)"
              type="number"
              fullWidth
              margin="normal"
              value={secondaryForm.area}
              onChange={this.updateSecondaryFormField("area")}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={this.closeSecondaryCityDialog} disabled={secondarySaving}>
              Anulează
            </Button>
            <Button onClick={this.saveSecondaryCity} color="primary" variant="contained" disabled={secondarySaving}>
              Salvează
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}
